import {StrictMode, type ComponentType} from 'react'
import {createRoot} from 'react-dom/client'

import {ChakraProvider} from '@chakra-ui/react'
import {createInertiaApp} from '@inertiajs/react'

import theme from '@app/theme'
import ErrorBoundary from '@components/ErrorBoundary'
import OverlayManager from '@components/OverlayManager'
import Toaster from '@components/Toaster'

import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

import * as Sentry from '@sentry/react'
import DashboardLayout from '@components/DashboardLayout/DashboardLayout'
import PageLayout from './components/PageLayout/PageLayout'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_REACT_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
})

window.Pusher = Pusher

window.Echo = new Echo({
  broadcaster: 'reverb',
  key: import.meta.env.VITE_REVERB_APP_KEY,
  cluster: '',
  wsHost: import.meta.env.VITE_REVERB_HOST,
  wsPort: import.meta.env.VITE_REVERB_PORT,
  wssPort: import.meta.env.VITE_REVERB_PORT,
  forceTLS: false,
  encrypted: true,
  disableStats: true,
  enabledTransports: ['ws', 'wss'],
})

const dashboardPrefixes = ['/admin', '/agency']

const pagesWithoutLayout = [
  '/login',
  '/password',
  '/forgot-password',
  '/admin/pickup-orders',
]

createInertiaApp({
  async resolve(name) {
    const sections = import.meta.glob<{default: ComponentType<any>}>(
      './sections/**/entry.ts',
    )

    const load = sections[`./sections/${name}/entry.ts`]
    if (!load) throw new Error(`Unknown Inertia page: ${name}`)
    return (await load()).default
  },
  setup({el, App, props}) {
    if (!el) throw new Error('Inertia root element was not found.')
    createRoot(el).render(
      <StrictMode>
        <ChakraProvider value={theme}>
          <App {...props}>
            {({Component, key, props}) => {
              const Layout = getLayout(!!props.viewer)

              return (
                <>
                  <Layout>
                    <Component key={key} {...props} />
                  </Layout>
                  <ErrorBoundary FallbackComponent={() => null}>
                    <OverlayManager />
                  </ErrorBoundary>
                  <Toaster />
                </>
              )
            }}
          </App>
        </ChakraProvider>
      </StrictMode>,
    )
  },
})

function getLayout(hasViewer = false) {
  const isDashboard = dashboardPrefixes.some((prefix) =>
    window.location.pathname.startsWith(prefix),
  )

  const withoutLayout = pagesWithoutLayout.some(
    (page) =>
      window.location.pathname === page ||
      window.location.pathname.startsWith(page),
  )

  if (isDashboard && hasViewer && !withoutLayout) {
    return DashboardLayout
  } else if (!withoutLayout && !isDashboard) {
    return PageLayout
  }

  return EmptyLayout
}

function EmptyLayout({children}: {children: React.ReactNode}) {
  return <>{children}</>
}
